import { useEffect, useState } from 'react'

import { useSearchPageStore } from 'Pods/SearchPage/Store'
import { fetchResultCount } from 'Pods/SearchPage/Store/actions/results'
import { mergeKeywordTerms } from 'Pods/SearchPage/Store/keywordTerms'

// Same pause as the keyword commit (KEYWORD_COMMIT_DELAY), so typing a term costs one request.
export const COUNT_DELAY = 400

const isSameList = (list, other) => list.length === other.length && list.every((value, index) => value === other[index])

/**
 * Result count for what the search-within panel would apply, so Apply / Search never promise the
 * count of the results already on the page (Edvardas, Oct-13: the count shows "how your filters
 * change it"). While nothing is staged it is the applied count.
 *
 * @param {{ terms: string[], tags: string[] } | null} staged - copy edited below lg; null from lg
 * @param {string} draft - text in the input, which Search applies along with the staged terms
 * @returns {number | null} null while the count for the staged terms is loading
 */
function useStagedResultCount(staged, draft) {
  const appliedCount = useSearchPageStore(state => state.meta.totalCount) || 0
  const keyword = useSearchPageStore(state => state.keyword)
  const keywordTerms = useSearchPageStore(state => state.keywordTerms)
  const tags = useSearchPageStore(state => state.tags)
  const [preview, setPreview] = useState(null)

  const terms = staged ? mergeKeywordTerms(staged.terms, draft) : keywordTerms
  const isApplied = !staged ||
    (keyword === '' && isSameList(terms, keywordTerms) && isSameList(staged.tags, tags))
  const requestKey = isApplied ? null : JSON.stringify({ terms, tags: staged.tags })

  useEffect(() => {
    if (requestKey === null) return undefined

    let isCurrent = true
    const timer = setTimeout(() => {
      const overrides = JSON.parse(requestKey)
      fetchResultCount({ keyword: '', keywordTerms: overrides.terms, tags: overrides.tags })
        .then((count) => {
          if (isCurrent) setPreview({ key: requestKey, count })
        })
        .catch(() => {})
    }, COUNT_DELAY)

    return () => {
      isCurrent = false
      clearTimeout(timer)
    }
  }, [requestKey])

  if (requestKey === null) return appliedCount

  return preview?.key === requestKey ? preview.count : null
}

export default useStagedResultCount
